<template>
    <div class="campaign-show">
        <div class="campaign-show__header">
            <div>
                <h1 class="campaign-show__title">{{ campaign.name }}</h1>
                <span class="campaign-show__badge"
                    :class="`campaign-show__badge--${getDisplayStatus(campaign.status)}`">
                    {{ getDisplayStatusLabel(campaign.status) }}
                </span>
            </div>
            <a :href="`/guaranteed/campaigns/${campaign.id}/edit`" class="btn btn--primary">
                Редактировать
            </a>
        </div>

        <div class="campaign-show__tabs">
            <button v-for="tab in tabs" :key="tab.key" :class="['tab', { active: activeTab === tab.key }]"
                @click="activeTab = tab.key">
                {{ tab.label }}
            </button>
        </div>

        <div class="campaign-show__content">
            <!-- Вкладка Настройки -->
            <div v-if="activeTab === 'settings'" class="tab-content">
                <div class="info-section">
                    <h3>Основная информация</h3>
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="info-label">Период:</span>
                            <span class="info-value">{{ formatPeriod(campaign.period_start, campaign.period_end)
                                }}</span>
                        </div>
                        <div class="info-item" v-if="campaign.comment">
                            <span class="info-label">Комментарий:</span>
                            <span class="info-value">{{ campaign.comment }}</span>
                        </div>
                    </div>
                </div>

                <div class="info-section">
                    <h3>Билборды</h3>
                    <div v-if="campaign.frames && campaign.frames.length > 0" class="frames-list">
                        <div v-for="frame in campaign.frames" :key="frame.campaign_frame_id" class="frame-item">
                            <div class="frame-info">
                                <strong>{{ frame.frame?.address || frame.frame?.gid || 'Неизвестно' }}</strong>
                                <span class="frame-meta">
                                    Повторений: {{ frame.repetitions }},
                                    Длительность: {{ frame.duration }}мс,
                                    Стоимость: {{ formatPrice(frame.cost) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-state">Нет выбранных билбордов</div>
                </div>
            </div>

            <!-- Вкладка Объявления -->
            <div v-if="activeTab === 'advertisements'" class="tab-content">
                <div class="advertisements-header">
                    <h3>Объявления</h3>
                    <a :href="`/guaranteed/campaigns/${campaign.id}/advertisements/create`" class="btn btn--primary"
                        v-if="canCreateAd">
                        + Создать объявление
                    </a>
                </div>
                <div v-if="campaign.advertisements && campaign.advertisements.length > 0" class="advertisements-list">
                    <div v-for="ad in campaign.advertisements" :key="ad.id" class="advertisement-card">
                        <div class="ad-card-header">
                            <div>
                                <strong>{{ ad.name || `Объявление #${ad.id}` }}</strong>
                                <span class="ad-badge" :class="`ad-badge--${ad.status}`">
                                    {{ ad.status }}
                                </span>
                            </div>
                            <a :href="`/guaranteed/campaigns/${campaign.id}/advertisements/${ad.id}/edit`"
                                class="btn btn--small">
                                Редактировать
                            </a>
                        </div>
                        <div class="ad-card-body">
                            <div class="ad-info">
                                <span>Период: {{ formatPeriod(ad.period_start, ad.period_end) }}</span>
                                <span v-if="ad.creatives && ad.creatives.length > 0">
                                    Креативов: {{ ad.creatives.length }}
                                </span>
                            </div>
                            <div v-if="ad.creatives && ad.creatives.length > 0" class="ad-creatives">
                                <div v-for="creative in ad.creatives" :key="creative.id" class="creative-preview">
                                    <img v-if="creative.type === 'picture'" :src="creative.url" :alt="creative.name" />
                                    <video v-else :src="creative.url" preload="metadata"></video>
                                    <span class="creative-name">{{ creative.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="empty-state">
                    Нет объявлений.
                    <a :href="`/guaranteed/campaigns/${campaign.id}/advertisements/create`" v-if="canCreateAd">
                        Создать первое объявление
                    </a>
                </div>
            </div>

            <!-- Вкладка Статистика -->
            <div v-if="activeTab === 'stats'" class="tab-content">
                <div class="empty-state">
                    Статистика показов будет доступна здесь
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapCampaignStatus, getCampaignStatusLabel } from '../../../utils/campaignStatus.js';
import { formatDateOnlyInRtbTZ } from '../../../utils/timezones';

export default {
    name: 'CampaignShow',
    props: {
        campaign: {
            type: Object,
            required: true,
        },
        creatives: {
            type: Array,
            default: () => [],
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            activeTab: 'settings',
            tabs: [
                { key: 'settings', label: 'Настройки' },
                { key: 'advertisements', label: 'Объявления' },
                { key: 'stats', label: 'Статистика' },
            ],
        };
    },
    computed: {
        canCreateAd() {
            // Проверяем флаг из бэкенда (статус >= reserve) И наличие креативов
            const canCreate = this.campaign.can_create_advertisement === true;
            const hasAvailableCreatives = Array.isArray(this.creatives) && this.creatives.length > 0;
            return canCreate && hasAvailableCreatives;
        },
    },
    methods: {
        formatPeriod(start, end) {
            const s = formatDateOnlyInRtbTZ(start);
            const e = formatDateOnlyInRtbTZ(end);
            if (!s && !e) return '—';
            if (s && e) return `${s} — ${e}`;
            return s || e || '—';
        },
        formatPrice(price) {
            const nf = new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                maximumFractionDigits: 2,
            });
            const n = Number(price);
            if (!Number.isFinite(n)) return nf.format(0);
            return nf.format(n);
        },
        getDisplayStatus(status) {
            return mapCampaignStatus(status);
        },
        getDisplayStatusLabel(status) {
            const displayStatus = mapCampaignStatus(status);
            return getCampaignStatusLabel(displayStatus);
        },
    },
};
</script>

<style lang="scss" scoped>
.campaign-show {
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2rem;
    }

    &__title {
        font-size: 2rem;
        font-weight: 600;
        color: #0f172a;
        margin: 0 1rem 0 0;
        display: inline-block;
    }

    &__badge {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: capitalize;

        // Упрощенные статусы
        &--active {
            background: #d4edda;
            color: #155724;
        }

        &--paused {
            background: #fff3cd;
            color: #856404;
        }

        &--finished {
            background: #f8d7da;
            color: #721c24;
        }

        &--moderation {
            background: #fff3cd;
            color: #856404;
        }

        // Старые статусы для обратной совместимости
        &--activated,
        &--booked,
        &--approve {
            background: #d4edda;
            color: #155724;
        }

        &--review,
        &--pending_approval {
            background: #fff3cd;
            color: #856404;
        }

        &--rejected,
        &--cancelled {
            background: #f8d7da;
            color: #721c24;
        }
    }

    &__tabs {
        display: flex;
        gap: 0.5rem;
        border-bottom: 2px solid #e0e0e0;
        margin-bottom: 2rem;
    }

    .tab {
        padding: 0.75rem 1.5rem;
        background: none;
        border: none;
        border-bottom: 3px solid transparent;
        font-weight: 500;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.2s;
        margin-bottom: -2px;

        &:hover {
            color: #0f172a;
        }

        &.active {
            color: #007bff;
            border-bottom-color: #007bff;
        }
    }

    &__content {
        background: #fff;
        border-radius: 8px;
        padding: 2rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .tab-content {
        min-height: 400px;
    }

    .info-section {
        margin-bottom: 2rem;

        h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #0f172a;
            margin-bottom: 1rem;
        }
    }

    .info-grid {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .info-item {
        display: flex;
        gap: 1rem;
    }

    .info-label {
        font-weight: 500;
        color: #6b7280;
        min-width: 150px;
    }

    .info-value {
        color: #0f172a;
    }

    .frames-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .frame-item {
        padding: 1rem;
        background: #f7f9fb;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
    }

    .frame-info {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .frame-meta {
        font-size: 0.875rem;
        color: #6b7280;
    }

    .advertisements-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;

        h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #0f172a;
            margin: 0;
        }
    }

    .advertisements-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .advertisement-card {
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 1.5rem;
        background: #fff;
    }

    .ad-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
    }

    .ad-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 500;
        margin-left: 0.5rem;

        &--approve {
            background: #d4edda;
            color: #155724;
        }

        &--pause {
            background: #fff3cd;
            color: #856404;
        }

        &--cancelled {
            background: #f8d7da;
            color: #721c24;
        }
    }

    .ad-card-body {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .ad-info {
        display: flex;
        gap: 1rem;
        font-size: 0.875rem;
        color: #6b7280;
    }

    .ad-creatives {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
    }

    .creative-preview {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-width: 200px;
    }

    .creative-preview img,
    .creative-preview video {
        width: 100%;
        height: auto;
        border-radius: 4px;
    }

    .creative-name {
        font-size: 0.875rem;
        color: #6b7280;
    }

    .empty-state {
        text-align: center;
        padding: 3rem;
        color: #6b7280;
    }
}
</style>
